import { useState, useCallback } from 'react';
import { useDocumentPreview } from '../../api/bewerber';
import { Dokument, isDocumentPreviewable, isPdfMimeType, isImageMimeType } from '../../types/bewerber';
import { PdfViewer } from '../document/PdfViewer';
import { ImageViewer } from '../document/ImageViewer';
import { ZoomControls } from '../document/ZoomControls';

interface PflegenDocumentPanelProps {
  bewerberId: string;
  documents: Dokument[];
  selectedIndex: number;
  onSelectIndex: (index: number) => void;
}

interface ZoomState {
  zoom: number;
  zoomIn: () => void;
  zoomOut: () => void;
  resetZoom: () => void;
}

export function PflegenDocumentPanel({
  bewerberId,
  documents,
  selectedIndex,
  onSelectIndex,
}: PflegenDocumentPanelProps) {
  const [zoomState, setZoomState] = useState<ZoomState | null>(null);

  const currentDocument = documents[selectedIndex];
  const canPreview = currentDocument && isDocumentPreviewable(currentDocument.mimeType);

  const { data: previewUrl, isLoading, error } = useDocumentPreview(
    bewerberId,
    currentDocument?.id || '',
    !!currentDocument && canPreview
  );

  const handleZoomChange = useCallback((
    zoom: number,
    zoomIn: () => void,
    zoomOut: () => void,
    resetZoom: () => void
  ) => {
    setZoomState({ zoom, zoomIn, zoomOut, resetZoom });
  }, []);

  const handlePrev = () => {
    if (selectedIndex > 0) {
      onSelectIndex(selectedIndex - 1);
    }
  };

  const handleNext = () => {
    if (selectedIndex < documents.length - 1) {
      onSelectIndex(selectedIndex + 1);
    }
  };

  if (documents.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center bg-gray-50 rounded-lg border border-gray-200">
        <svg className="w-16 h-16 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <p className="mt-4 text-gray-500 text-sm">Keine Dokumente vorhanden</p>
      </div>
    );
  }

  return (
    <div className="h-full relative overflow-hidden bg-gray-200 rounded-lg">
      {/* Floating Navigation Overlay - independent hover */}
      <div className="group absolute top-0 left-0 right-0 z-10">
        <div className="flex items-center justify-between px-3 py-3 bg-gradient-to-b from-black/50 via-black/25 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <button
          type="button"
          onClick={handlePrev}
          disabled={selectedIndex === 0}
          className="p-1.5 rounded text-white hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed drop-shadow-md"
          title="Vorheriges Dokument"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <div className="text-center flex-1 min-w-0 px-2">
          <p className="text-sm font-medium text-white truncate drop-shadow-md" title={currentDocument?.originalFilename}>
            {currentDocument?.originalFilename}
          </p>
          <p className="text-xs text-white/80 drop-shadow-md">
            {selectedIndex + 1} / {documents.length}
          </p>
        </div>

        <button
          type="button"
          onClick={handleNext}
          disabled={selectedIndex === documents.length - 1}
          className="p-1.5 rounded text-white hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed drop-shadow-md"
          title="Nächstes Dokument"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
        </div>
      </div>

      {/* Document Preview */}
      <div className="h-full">
        {!canPreview ? (
          <div className="h-full flex flex-col items-center justify-center">
            <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="mt-4 text-gray-600 font-medium">Vorschau nicht verfügbar</p>
            <p className="mt-1 text-sm text-gray-500">{currentDocument?.originalFilename}</p>
          </div>
        ) : isLoading ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-3 text-sm text-gray-500">Dokument wird geladen...</p>
            </div>
          </div>
        ) : error ? (
          <div className="h-full flex flex-col items-center justify-center">
            <svg className="w-16 h-16 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <p className="mt-4 text-red-600 font-medium">Fehler beim Laden</p>
            <p className="mt-1 text-sm text-gray-500">Das Dokument konnte nicht geladen werden.</p>
          </div>
        ) : previewUrl ? (
          isPdfMimeType(currentDocument.mimeType) ? (
            <PdfViewer
              url={previewUrl}
              filename={currentDocument.originalFilename}
              className="h-full"
              onZoomChange={handleZoomChange}
            />
          ) : isImageMimeType(currentDocument.mimeType) ? (
            <ImageViewer
              url={previewUrl}
              filename={currentDocument.originalFilename}
              className="h-full"
              onZoomChange={handleZoomChange}
            />
          ) : null
        ) : null}
      </div>

      {/* Zoom controls - independent hover */}
      {zoomState && canPreview && previewUrl && !isLoading && !error && (
        <div className="group absolute bottom-4 left-1/2 -translate-x-1/2 z-10">
          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <ZoomControls
              zoom={zoomState.zoom}
              onZoomIn={zoomState.zoomIn}
              onZoomOut={zoomState.zoomOut}
              onReset={zoomState.resetZoom}
            />
          </div>
        </div>
      )}
    </div>
  );
}
